import { EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RelativeTime } from '@/shared/components/ui/RelativeTime/RelativeTime';

import { type ViolationEvidenceProps } from './ViolationEvidence.types';
import { useViolationEvidenceLogic } from './useViolationEvidenceLogic';

/**
 * What a reviewer is deciding about.
 *
 * Two arms, because a violation has two kinds of subject since P27a1 and they
 * are not two renderings of one thing. A **message** case is the member's own
 * words — the reason this panel exists at all, since chat is member-to-member by
 * construction (N13) and no other admin surface shows a conversation. A
 * **behavioural** case has no words: its evidence is a count, the threshold it
 * crossed and the window it crossed it in, and all three are shown together
 * because any one of them alone is unreadable ("140 likes" means nothing without
 * "in an hour, against a limit of 60").
 */
export function ViolationEvidence({ subject }: ViolationEvidenceProps) {
  const { t } = useTranslation('violations');
  const view = useViolationEvidenceLogic(subject);

  if (view.kind === 'message') {
    return (
      <section className="flex flex-col gap-xs">
        <h3 className="text-caption font-medium text-foreground">{t('review.evidence')}</h3>
        {view.isGone ? (
          <p className="text-body text-muted-foreground">{t('review.evidenceGone')}</p>
        ) : (
          // The member's own words, in full and unreflowed — the dialog body is
          // the one scrolling region, so a long message is readable rather than
          // truncated.
          <p className="whitespace-pre-wrap break-words text-body">{view.body}</p>
        )}
        {view.isHidden ? (
          <span className="flex items-center gap-2xs text-caption text-muted-foreground">
            <EyeOff aria-hidden className="h-3.5 w-3.5 shrink-0" />
            {t('review.evidenceHidden')}
          </span>
        ) : null}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-xs">
      <h3 className="text-caption font-medium text-foreground">{t('review.behaviorEvidence')}</h3>
      <p className="text-body">{t(`review.behaviorRule.${view.rule}`)}</p>
      {/* A definition list rather than a sentence: the three numbers are read
          against each other, and a reviewer scanning several cases finds them in
          the same place every time. */}
      <dl className="grid grid-cols-[auto_1fr] gap-x-md gap-y-2xs text-caption">
        <dt className="text-muted-foreground">{t('review.behaviorObserved')}</dt>
        <dd className="font-medium text-foreground">{view.observedCount}</dd>
        <dt className="text-muted-foreground">{t('review.behaviorThreshold')}</dt>
        <dd className="font-medium text-foreground">{view.threshold}</dd>
        <dt className="text-muted-foreground">{t('review.behaviorWindow')}</dt>
        <dd className="font-medium text-foreground">
          {t(`review.behaviorWindowValue.${view.window.unit}`, { count: view.window.count })}
        </dd>
        <dt className="text-muted-foreground">{t('review.behaviorWindowStart')}</dt>
        <dd className="font-medium text-foreground">
          <RelativeTime value={view.startedAt} />
        </dd>
      </dl>
      <p className="text-caption text-muted-foreground">{t('review.behaviorHint')}</p>
    </section>
  );
}
